import { AddressType, ViewName } from '@uplift/types';
import { useStates } from '@uplift/queries';
import { useCastAddress } from '../../molecules';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { AddressForm } from './address-form.component';
import { useAddressVerificationContext } from '../address-verification-context';
import { AddressVerifyModal } from '../AddressVerifyModal/AddressVerifyModal.component';
import { AddressQuickSearchInput } from '../AddressQuickSearchInput/AddressQuickSearchInput.component';
import { AddressSearchModal } from '../AddressSearchModal';
import { isObjectKey } from '@uplift/utils';

export type AddressQuickSearchFormProps = {
  label?: React.ReactNode;
  getFieldName?: (fieldName: keyof AddressType) => string;
};

const getAllFieldNames = (getFieldName: AddressQuickSearchFormProps['getFieldName']) => {
  const fieldKeyToName = {
    address1: 'address1',
    address2: 'address2',
    city: 'city',
    state: 'state',
    zip: 'zip'
  };

  if (getFieldName) {
    Object.keys(fieldKeyToName)
      // Object.keys() does not return (keyof T)[]
      .filter((x: string | number | symbol): x is keyof typeof fieldKeyToName => isObjectKey(x, fieldKeyToName))
      .forEach(keyName => {
        fieldKeyToName[keyName] = getFieldName(keyName);
      });
  }

  return fieldKeyToName;
};

export const StandaloneAddressQuickSearchForm: React.VFC<AddressQuickSearchFormProps> = props => {
  const fieldNames = getAllFieldNames(props.getFieldName);
  const { state: { shouldReverifyAddress, parsedVerifiedAddress }, dispatch } = useAddressVerificationContext();
  const { states } = useStates();
  const { castAddressToAddressWithStateAsId, castAddressWithStateAsFullOrShortName } = useCastAddress(states);
  const formik = useFormikContext();

  const values = useMemo(
    () => (({
      address1: formik.getFieldMeta(fieldNames.address1).value ?? '',
      address2: formik.getFieldMeta(fieldNames.address2).value ?? '',
      city: formik.getFieldMeta(fieldNames.city).value ?? '',
      state: formik.getFieldMeta(fieldNames.state).value ?? '',
      zip: formik.getFieldMeta(fieldNames.zip).value ?? ''
    }) as AddressType),
    [ fieldNames, formik ]
  );

  const quickSearchError = useMemo(() => {
    const address1 = formik.getFieldMeta(fieldNames.address1);

    return address1.touched && address1.error ? 'Enter your address' : '';
  }, [ fieldNames.address1, formik ]);

  const [ _view, setView ] = useState<ViewName>('search');

  const view: ViewName = useMemo(() => {
    if (shouldReverifyAddress) {
      return 'reverify';
    } else if (parsedVerifiedAddress) {
      return 'manual';
    } else {
      return _view;
    }
  }, [ _view, parsedVerifiedAddress, shouldReverifyAddress ]);

  const { setFieldValue, setFieldTouched } = formik;

  const setAddressFieldsToFormik = useCallback((address: AddressType) => {
    Object.keys(address).forEach((key) => {
      const fieldKey = key as keyof AddressType;

      setFieldValue(fieldNames[fieldKey], address[fieldKey]);
    });

    // set all address fields as touched after updating values - https://github.com/jaredpalmer/formik/issues/2059#issuecomment-612733378
    setTimeout(() => Object.keys(address).forEach((key) => {
      const fieldKey = key as keyof AddressType;

      setFieldTouched(fieldNames[fieldKey]);
    }));
  }, [ setFieldValue, fieldNames, setFieldTouched ]);


  const addressSelectHandler = useCallback((address: AddressType) => {
    const addressWithStateId = castAddressToAddressWithStateAsId(address);

    dispatch({
      type: 'setParsedVerifiedAddress',
      payload: {
        parsedVerifiedAddress: addressWithStateId
      }
    });

    setAddressFieldsToFormik(addressWithStateId);
  }, [ castAddressToAddressWithStateAsId, dispatch, setAddressFieldsToFormik ]);

  const closeVerifyModalHandler = useCallback(() => {
    dispatch({
      type: 'setShouldReverifyAddress',
      payload: {
        shouldReverifyAddress: false
      }
    });
  }, [ dispatch ]);

  const { submitForm } = formik;
  const confirmAddressHandler = useCallback((address: AddressType) => {
    closeVerifyModalHandler();
    addressSelectHandler(address);
    submitForm();
  }, [ addressSelectHandler, closeVerifyModalHandler, submitForm ]);

  const reverifyModal = useMemo(() => {
    if (view !== 'reverify') {
      return null;
    }

    const addressVerify = castAddressWithStateAsFullOrShortName(values, 'shortName');

    return (
      <AddressVerifyModal
        addressVerify={addressVerify}
        open
        onClose={closeVerifyModalHandler}
        onAddressConfirm={confirmAddressHandler}
      />
    );
  }, [ castAddressWithStateAsFullOrShortName, closeVerifyModalHandler, confirmAddressHandler, values, view ]);

  const onCloseModal = () => {
    setView('search');
    formik.setFieldTouched(fieldNames.address1);
  };

  return (
    <Box>
      {Array<ViewName>('manual', 'reverify').includes(view) && (
        <AddressForm
          fieldNames={fieldNames}
          address2Open={!!values.address2}
        />
      )}
      {Array<ViewName>('search', 'modal').includes(view) && (
        <AddressQuickSearchInput
          onFocus={() => setView('modal')}
          label={props.label}
          errorText={quickSearchError}
          isModalOpen={view === 'modal'}
        />
      )}
      <AddressSearchModal
        onClose={onCloseModal}
        open={view === 'modal'}
        onManualSelect={() => setView('manual')}
        onAddressSelect={addressSelectHandler}
        color="secondary"
      />
      {reverifyModal}
    </Box>
  );
};
